/**
 * Auth Provider - Authentication state management
 *
 * Handles:
 * - Firebase Auth state listening
 * - Auto-create user document on first login
 * - Real-time user document subscription
 * - Automatic logout for deactivated users
 * - Domain-based access control
 */

import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { onAuthStateChanged, type User as FirebaseUser } from 'firebase/auth';
import { auth, signInWithMicrosoft, signOut as firebaseSignOut } from '@shared/api';
import { AuthService, roleFromClaims } from '@features/auth';
import { SessionContext, type SessionContextValue, type SessionUser } from '@entities/session';
import { LoadingSpinner } from '@shared/ui/loading-spinner';
import { createLogger } from '@shared/lib';

const log = createLogger('AuthProvider');

interface AuthProviderProps {
  children: ReactNode;
}

// Allowed email domains for login
const ALLOWED_DOMAINS = ['synnovation-inc.de', 'chancen-pilot.de'];

/**
 * Check if email domain is allowed
 */
function isEmailDomainAllowed(email: string | null): boolean {
  if (!email) return false;
  const domain = email.split('@')[1]?.toLowerCase();
  return domain ? ALLOWED_DOMAINS.includes(domain) : false;
}

/**
 * Auth provider component
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Sign in handler
  const signIn = useCallback(async () => {
    try {
      setError(null);
      await signInWithMicrosoft();
    } catch (err) {
      log.error('Sign in error:', err);
      setError(err instanceof Error ? err : new Error('Login fehlgeschlagen'));
      throw err;
    }
  }, []);

  // Sign out handler
  const signOut = useCallback(async () => {
    try {
      await firebaseSignOut();
      setUser(null);
      setFirebaseUser(null);
      setError(null);
    } catch (err) {
      log.error('Sign out error:', err);
      throw err;
    }
  }, []);

  // Listen to Firebase Auth state changes
  useEffect(() => {
    let unsubscribeUser: (() => void) | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (fbUser) => {
      // Clean up previous user subscription
      if (unsubscribeUser) {
        unsubscribeUser();
        unsubscribeUser = null;
      }

      setFirebaseUser(fbUser);

      if (!fbUser) {
        // User signed out
        setUser(null);
        setLoading(false);
        setError(null);
        return;
      }

      try {
        // Check if user document exists (e.g. educators created via invitation)
        let userDoc = await AuthService.getUser(fbUser.uid);

        if (!userDoc && !isEmailDomainAllowed(fbUser.email)) {
          // No user doc and no company domain. Educators never pass through
          // this state: redeemInvitation writes their document before it
          // hands out a session.
          log.info('Domain not allowed:', fbUser.email);
          setError(
            new Error(
              'Zugriff verweigert. Ihre E-Mail-Domain ist nicht für diese Anwendung freigegeben.'
            )
          );
          await firebaseSignOut();
          setUser(null);
          setLoading(false);
          return;
        }

        if (!userDoc) {
          // First login - create user document.
          //
          // Die Rolle steht im Token, nicht in dieser Zeile. Bis zum
          // 10.09.2026 schrieb `createUser` fest `employee` — wem sein
          // Dokument fehlte, den stufte die eigene Anmeldung herab.
          const { claims } = await fbUser.getIdTokenResult();
          log.info('Creating user document for:', fbUser.email);
          userDoc = await AuthService.createUser(
            fbUser.uid,
            fbUser.email ?? '',
            fbUser.displayName ?? fbUser.email?.split('@')[0] ?? 'User',
            roleFromClaims(claims)
          );
        }

        // Check if user is active
        if (!userDoc.active) {
          log.info('User is deactivated:', fbUser.email);
          setError(
            new Error(
              'Ihr Benutzerkonto ist deaktiviert. Bitte wenden Sie sich an einen Administrator.'
            )
          );
          await firebaseSignOut();
          setUser(null);
          setLoading(false);
          return;
        }

        // Subscribe to user document for real-time updates
        unsubscribeUser = AuthService.subscribeToUser(
          fbUser.uid,
          (updatedUser) => {
            if (updatedUser) {
              // Check if user was deactivated
              if (!updatedUser.active) {
                log.info('User was deactivated:', updatedUser.email);
                setError(new Error('Ihr Benutzerkonto wurde deaktiviert.'));
                firebaseSignOut();
                setUser(null);
                return;
              }
              setUser(updatedUser);
            } else {
              // User document was deleted
              setUser(null);
            }
          },
          (err) => {
            log.error('User subscription error:', err);
          }
        );

        setUser(userDoc);
        setError(null);
      } catch (err) {
        log.error('Error loading user:', err);
        setError(err instanceof Error ? err : new Error('Fehler beim Laden des Benutzers'));
      } finally {
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeUser) {
        unsubscribeUser();
      }
    };
  }, []);

  // Show loading spinner while auth state is being determined
  if (loading) {
    return <LoadingSpinner message="Anmeldung wird geprüft..." />;
  }

  const value: SessionContextValue = {
    user,
    firebaseUser,
    loading,
    error,
    signIn,
    signOut,
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
